import { useEffect, useRef, useState } from 'react';
import { useTheme } from 'styled-components';

import { ReactComponent as ChevronSVG } from '../../assets/icons/chevronLeft.svg';
import { getMonthsDates } from '../../utils/date';
import ScheduleTag from '../ScheduleTag';
import {
  DatesWithButtonWrapperStyled,
  DatesWrapperStyled,
  IndividualDateStyled,
  ScheduleCalenderContinerStyled,
  ScheduleTagContainerStyled,
} from './style';
type StatusType = 'Pending' | 'Active' | 'Completed';

type EventType = {
  userId: string;
  avatar: string;
  title: string;
  status: StatusType;
};

type Props = {
  year?: number;
  month?: number;
  events?: EventType[];
  onDateSelecction?: (data: DatesType) => void;
  defaultDate?: number;
};

type DatesType = {
  date: number;
  day: string;
  fullDay: string;
};

const today = new Date();

const ScheduleCalendar = ({
  month = today.getMonth() + 1,
  year = today.getFullYear(),
  onDateSelecction,
  defaultDate = today.getDate(),
  events,
}: Props) => {
  const dateContainerRef = useRef<HTMLDivElement>(null);
  const dateRef = useRef<HTMLDivElement>(null);
  const [dates, setDates] = useState<DatesType[]>([]);
  const [selectedDate, setSelectedDate] = useState(defaultDate);
  const [buttonStates, setButtonStates] = useState({
    leftBtn: false,
    rightBtn: true,
  });
  const theme = useTheme();

  useEffect(() => {
    const listOfDates = getMonthsDates(year, month);
    setDates(listOfDates);
  }, [month, year]);

  const scrollToElement = () => {
    const { current } = dateRef;
    if (current !== null) {
      current.scrollIntoView({ behavior: 'smooth' });
    }
  };
  useEffect(scrollToElement, [dates]);

  useEffect(() => {
    const scrollingElement = dateContainerRef?.current;
    const handleOnScroll = () => {
      const scrollLeft = scrollingElement?.scrollLeft || 0;
      const clientWidth = scrollingElement?.clientWidth || 0;
      const scrollWidth = scrollingElement?.scrollWidth || 0;
      if (scrollLeft <= 35) {
        setButtonStates((prev) => ({ ...prev, leftBtn: false }));
      } else {
        setButtonStates((prev) => ({ ...prev, leftBtn: true }));
      }

      if (scrollLeft + clientWidth === scrollWidth) {
        setButtonStates((prev) => ({ ...prev, rightBtn: false }));
      } else {
        setButtonStates((prev) => ({ ...prev, rightBtn: true }));
      }
    };
    if (scrollingElement) {
      scrollingElement.addEventListener('scroll', handleOnScroll);
    }

    return () => {
      scrollingElement?.removeEventListener('scroll', handleOnScroll);
    };
  }, []);

  const handleDateSelection = (date: DatesType) => {
    setSelectedDate(date.date);
    if (onDateSelecction) {
      onDateSelecction(date);
    }
  };

  const handleOnLeftRightArrowClick = (type: 'left' | 'right') => {
    const scrollingElement = dateContainerRef?.current;
    if (scrollingElement) {
      if (type === 'left') {
        scrollingElement.scrollBy({ left: -50 });
      }
      if (type === 'right') {
        scrollingElement.scrollBy({ left: 38 });
      }
    }
  };

  return (
    <ScheduleCalenderContinerStyled>
      <DatesWithButtonWrapperStyled>
        {buttonStates.leftBtn && (
          <IndividualDateStyled
            onClick={() => handleOnLeftRightArrowClick('left')}
          >
            <ChevronSVG className="btn-icon left" />
          </IndividualDateStyled>
        )}
        <DatesWrapperStyled ref={dateContainerRef}>
          {Array.isArray(dates)
            ? dates.map((lDate, idx) => {
                if (selectedDate === lDate.date) {
                  return (
                    <IndividualDateStyled
                      ref={dateRef}
                      key={`${lDate.date}-${idx}`}
                      className={`${
                        selectedDate === lDate.date ? 'selected' : ''
                      }`}
                      onClick={() => handleDateSelection(lDate)}
                    >
                      {lDate.date}
                    </IndividualDateStyled>
                  );
                }
                return (
                  <IndividualDateStyled
                    key={`${lDate.date}-${idx}`}
                    className={`${
                      selectedDate === lDate.date ? 'selected' : ''
                    }`}
                    onClick={() => handleDateSelection(lDate)}
                  >
                    {lDate.date}
                  </IndividualDateStyled>
                );
              })
            : []}
        </DatesWrapperStyled>
        {buttonStates.rightBtn && (
          <IndividualDateStyled
            onClick={() => handleOnLeftRightArrowClick('right')}
          >
            <ChevronSVG className="btn-icon right" />
          </IndividualDateStyled>
        )}
      </DatesWithButtonWrapperStyled>

      <ScheduleTagContainerStyled>
        {Array.isArray(events) &&
          events.map((event, idx) => (
            <ScheduleTag
              key={idx}
              avatar={event.avatar}
              title={event.title}
              bgColor={theme.colors.darkViolet.faded}
              status={event.status}
              textColor={theme.colors.darkViolet.strong}
              className="schedule-tag"
            />
          ))}
      </ScheduleTagContainerStyled>
    </ScheduleCalenderContinerStyled>
  );
};

export default ScheduleCalendar;
